import {
  Edit,
  SimpleForm,
  TextInput,
  SelectInput,
  useRecordContext,
  Toolbar,
  SaveButton,
  DeleteButton,
  useNotify,
  useRedirect,
} from "react-admin";
import { Box, Typography, Paper, Chip, Divider, Alert } from "@mui/material";
import { Image, Video, Calendar, HardDrive, Maximize, Tag, CheckCircle, AlertCircle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

// Service tags that the website expects for service images
const SERVICE_TAGS = [
  { key: "bathroom", label: "Bathroom Renovations" },
  { key: "kitchen", label: "Kitchen Remodeling" },
  { key: "renovation", label: "Whole-Home Renovations" },
  { key: "woodwork", label: "Carpentry & Custom Woodwork" },
  { key: "handyman", label: "24/7 Handyman & Emergency Repair" },
];

// Category choices
const categoryChoices = [
  { id: "portfolio", name: "Portfolio" },
  { id: "hero", name: "Hero" },
  { id: "testimonial", name: "Testimonial" },
  { id: "team", name: "Team" },
  { id: "service", name: "Service" },
  { id: "blog", name: "Blog" },
  { id: "gallery", name: "Gallery" },
  { id: "other", name: "Other" },
];

// Use VITE_API_URL for production (split architecture: Vercel → Railway)
const API_URL = import.meta.env.VITE_API_URL || "";

/**
 * Hook to fetch all service media and determine which tags are in use
 */
function useServiceTagsStatus(currentMediaId?: string | number) {
  return useQuery({
    queryKey: ["service-tags-status", currentMediaId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/public/media?category=service`);
      if (!res.ok) throw new Error("Failed to fetch service media");
      const serviceMedia = await res.json();
      
      // Build a map of which tags are in use and by which media
      const tagUsage: Record<string, { id: string; title: string } | null> = {};
      
      SERVICE_TAGS.forEach(({ key }) => {
        tagUsage[key] = null;
      });
      
      serviceMedia.forEach((media: { id: string; title: string | null; tags: string | null }) => {
        // Skip the current media being edited
        if (String(media.id) === String(currentMediaId)) return;
        
        const tags = media.tags?.split(",").map((t: string) => t.trim().toLowerCase()) || [];
        SERVICE_TAGS.forEach(({ key }) => {
          if (tags.includes(key)) {
            tagUsage[key] = { id: media.id, title: media.title || "Untitled" };
          }
        });
      });
      
      return tagUsage;
    },
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Service Tags Helper - Shows available tags and their usage status
 */
const ServiceTagsHelper = () => {
  const record = useRecordContext();
  const { data: tagUsage, isLoading } = useServiceTagsStatus(record?.id);
  
  if (!record || record.category !== "service") return null;
  
  const currentTags = record.tags?.split(",").map((t: string) => t.trim().toLowerCase()) || [];
  
  return (
    <Paper
      sx={{
        p: 2,
        mb: 2,
        backgroundColor: "rgba(34, 211, 238, 0.08)",
        border: "1px solid rgba(34, 211, 238, 0.3)",
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Tag size={18} color="#22d3ee" />
        <Typography variant="subtitle2" sx={{ color: "#22d3ee", fontWeight: 600 }}>
          Service Tags Reference
        </Typography>
      </Box>
      
      <Alert 
        severity="info" 
        sx={{ 
          mb: 2, 
          backgroundColor: "rgba(59, 130, 246, 0.1)",
          border: "1px solid rgba(59, 130, 246, 0.3)",
          "& .MuiAlert-message": { color: "#94a3b8" }
        }}
      >
        Add one of these tags to map this image to a service card on the website.
      </Alert>
      
      {isLoading ? (
        <Typography variant="body2" sx={{ color: "#94a3b8" }}>Loading tag status...</Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {SERVICE_TAGS.map(({ key, label }) => {
            const usedBy = tagUsage?.[key];
            const isUsedByCurrent = currentTags.includes(key);
            
            return (
              <Box
                key={key}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: 1,
                  borderRadius: 1,
                  backgroundColor: isUsedByCurrent 
                    ? "rgba(34, 197, 94, 0.15)" 
                    : usedBy 
                      ? "rgba(245, 158, 11, 0.1)" 
                      : "rgba(255,255,255,0.03)",
                  border: isUsedByCurrent 
                    ? "1px solid rgba(34, 197, 94, 0.4)"
                    : usedBy
                      ? "1px solid rgba(245, 158, 11, 0.3)"
                      : "1px solid rgba(255,255,255,0.1)",
                }}
              >
                {isUsedByCurrent ? (
                  <CheckCircle size={16} color="#22c55e" />
                ) : usedBy ? (
                  <AlertCircle size={16} color="#f59e0b" />
                ) : (
                  <Tag size={16} color="#64748b" />
                )}
                
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip
                      label={key}
                      size="small"
                      sx={{
                        backgroundColor: isUsedByCurrent 
                          ? "rgba(34, 197, 94, 0.2)" 
                          : "rgba(34, 211, 238, 0.2)",
                        color: isUsedByCurrent ? "#22c55e" : "#22d3ee",
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                      }}
                    />
                    <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                      → {label}
                    </Typography>
                  </Box>
                  
                  {usedBy && !isUsedByCurrent && (
                    <Typography variant="caption" sx={{ color: "#f59e0b", display: "block", mt: 0.5 }}>
                      ⚠️ Already used by: {usedBy.title}
                    </Typography>
                  )}
                </Box>
                
                {isUsedByCurrent && (
                  <Chip
                    label="Active"
                    size="small"
                    sx={{
                      backgroundColor: "rgba(34, 197, 94, 0.2)",
                      color: "#22c55e",
                      fontSize: "0.65rem",
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      )}
      
      <Divider sx={{ my: 2, borderColor: "rgba(34, 211, 238, 0.2)" }} />
      
      <Typography variant="caption" sx={{ color: "#64748b", display: "block" }}>
        💡 Tip: Copy a tag name above and paste it in the "Tags" field. 
        Multiple tags can be separated by commas.
      </Typography>
    </Paper>
  );
};

/**
 * Media preview sidebar showing the actual image/video
 */
const MediaPreview = () => {
  const record = useRecordContext();
  if (!record) return null;

  const isVideo = record.resourceType === "video";
  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Paper
      sx={{
        p: 3,
        backgroundColor: "rgba(34, 211, 238, 0.05)",
        border: "1px solid rgba(34, 211, 238, 0.2)",
        borderRadius: 2,
      }}
    >
      {/* Preview */}
      <Box sx={{ mb: 3 }}>
        {isVideo ? (
          <video
            src={record.secureUrl}
            controls
            style={{
              width: "100%",
              maxHeight: 300,
              borderRadius: 8,
              backgroundColor: "#000",
            }}
          />
        ) : (
          <img
            src={record.secureUrl}
            alt={record.title || "Media preview"}
            style={{
              width: "100%",
              maxHeight: 300,
              objectFit: "contain",
              borderRadius: 8,
              backgroundColor: "rgba(0,0,0,0.2)",
            }}
          />
        )}
      </Box>

      {/* Media Info */}
      <Typography variant="subtitle2" sx={{ color: "#22d3ee", mb: 2 }}>
        Media Details
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isVideo ? (
            <Video size={16} color="#a855f7" />
          ) : (
            <Image size={16} color="#22d3ee" />
          )}
          <Typography variant="body2" sx={{ color: "#94a3b8" }}>
            Type:
          </Typography>
          <Chip
            label={isVideo ? "Video" : "Image"}
            size="small"
            color={isVideo ? "secondary" : "primary"}
            variant="outlined"
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Maximize size={16} color="#22d3ee" />
          <Typography variant="body2" sx={{ color: "#94a3b8" }}>
            Dimensions:
          </Typography>
          <Typography variant="body2" sx={{ color: "#e2e8f0" }}>
            {record.width} × {record.height}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <HardDrive size={16} color="#22d3ee" />
          <Typography variant="body2" sx={{ color: "#94a3b8" }}>
            Size:
          </Typography>
          <Typography variant="body2" sx={{ color: "#e2e8f0" }}>
            {formatBytes(record.bytes)}
          </Typography>
        </Box>

        {isVideo && record.duration > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Calendar size={16} color="#22d3ee" />
            <Typography variant="body2" sx={{ color: "#94a3b8" }}>
              Duration:
            </Typography>
            <Typography variant="body2" sx={{ color: "#e2e8f0" }}>
              {Math.floor(record.duration / 60)}:{String(Math.floor(record.duration % 60)).padStart(2, "0")}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Calendar size={16} color="#22d3ee" />
          <Typography variant="body2" sx={{ color: "#94a3b8" }}>
            Format:
          </Typography>
          <Typography variant="body2" sx={{ color: "#e2e8f0", textTransform: "uppercase" }}>
            {record.format}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 2, borderColor: "rgba(34, 211, 238, 0.2)" }} />

      {/* URL Copy Section */}
      <Typography variant="subtitle2" sx={{ color: "#22d3ee", mb: 1 }}>
        Media URL
      </Typography>
      <Box
        component="code"
        sx={{
          display: "block",
          p: 1.5,
          backgroundColor: "rgba(0,0,0,0.3)",
          borderRadius: 1,
          fontSize: "0.75rem",
          wordBreak: "break-all",
          color: "#94a3b8",
          cursor: "pointer",
          "&:hover": {
            color: "#22d3ee",
          },
        }}
        onClick={() => {
          navigator.clipboard.writeText(record.secureUrl);
        }}
        title="Click to copy URL"
      >
        {record.secureUrl}
      </Box>
    </Paper>
  );
};

/**
 * Custom toolbar with save and delete buttons
 */
const EditToolbar = () => (
  <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
    <SaveButton />
    <DeleteButton />
  </Toolbar>
);

/**
 * MediaEdit - Form for editing media metadata
 */
export const MediaEdit = () => {
  const notify = useNotify();
  const redirect = useRedirect();

  const onSuccess = () => {
    notify("Media updated successfully", { type: "success" });
    redirect("list", "media");
  };

  const onError = (error: Error) => {
    notify(`Update failed: ${error.message}`, { type: "error" });
  };

  return (
    <Edit
      mutationMode="pessimistic"
      mutationOptions={{ onSuccess, onError }}
      sx={{
        "& .RaEdit-main": {
          backgroundColor: "#1a1a2e",
        },
      }}
    >
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, p: 2 }}>
        {/* Form Section */}
        <Box sx={{ flex: "1 1 400px", minWidth: 0 }}>
          <SimpleForm
            toolbar={<EditToolbar />}
            sx={{
              "& .MuiInputBase-root": {
                backgroundColor: "rgba(255,255,255,0.05)",
              },
              "& .MuiInputLabel-root": {
                color: "#94a3b8",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(34, 211, 238, 0.2)",
              },
              "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(34, 211, 238, 0.4)",
              },
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#22d3ee",
              },
            }}
          >
            <Box sx={{ mb: 3, width: "100%" }}>
              <Typography variant="h5" sx={{ color: "#22d3ee", mb: 1 }}>
                Edit Media
              </Typography>
              <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                Update media metadata and organization
              </Typography>
            </Box>

            <TextInput
              source="title"
              label="Title"
              fullWidth
              helperText="A descriptive title for this media"
            />

            <TextInput
              source="description"
              label="Description"
              fullWidth
              multiline
              rows={3}
              helperText="Detailed description (optional)"
            />

            <TextInput
              source="altText"
              label="Alt Text"
              fullWidth
              helperText="Alternative text for accessibility (important for SEO)"
            />

            <SelectInput
              source="category"
              label="Category"
              choices={categoryChoices}
              fullWidth
              helperText="Where will this media be used?"
            />

            <TextInput
              source="tags"
              label="Tags"
              fullWidth
              helperText="Comma-separated tags for organization"
            />

            <SelectInput
              source="featured"
              label="Featured"
              choices={[
                { id: "false", name: "No" },
                { id: "true", name: "Yes" },
              ]}
              fullWidth
              helperText="Featured media appears prominently on the site"
            />
          </SimpleForm>
        </Box>

        {/* Preview Section */}
        <Box sx={{ flex: "0 1 350px", minWidth: 280 }}>
          <ServiceTagsHelper />
          <MediaPreview />
        </Box>
      </Box>
    </Edit>
  );
};

export default MediaEdit;

